import { AJUDA_ESTOQUE } from "@/lib/ajuda/dialogos/estoque";
import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { numeroParaTexto, quantidadeParaNumero } from "@/lib/quantidade";
import { formatPriceCents, parsePriceInput } from "@/components/services/service-meta";
import { FornecedorSelect } from "@/components/partners/FornecedorSelect";
import { MaterialLineDialog } from "./MaterialLineDialog";
import { agruparMateriais } from "@/lib/agrupar-materiais";
import { useFeature } from "@/lib/use-feature";
import { usePermissao } from "@/lib/use-permissao";
import { hojeEmTexto, type DadosDaNota } from "@bookdental/schemas";
import { PagamentoDaCompra, type Pagamento } from "./PagamentoDaCompra";
import { ItensDaCompra, NOVO_MATERIAL, type Item } from "./ItensDaCompra";
import { NotaDoLancamento, type NotaDoLancamentoValor } from "@/components/financeiro/lancamentos/NotaDoLancamento";

const inputCls =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";
const labelCls = "mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground";
const soDigitos = (v: string | null | undefined) => (v ?? "").replace(/\D/g, "");

/**
 * Entrada de mercadoria: "recebi isto".
 *
 * Não é ordem de compra — não há rascunho, aprovação nem pendente de recebimento. Um laboratório
 * deste tamanho não aprova pedido internamente; a máquina de estados seria mais tela do que uso.
 */
export function RegistrarCompraDialog({
  aberto,
  materialInicial,
  quantidadeInicial,
  onFechar,
}: {
  aberto: boolean;
  materialInicial?: { id: string; sugerido: number } | null;
  quantidadeInicial?: number;
  onFechar: () => void;
}) {
  const utils = trpc.useUtils();
  const [fornecedor, setFornecedor] = useState("");
  const [documento, setDocumento] = useState("");
  const [atualizarPreco, setAtualizarPreco] = useState(true);
  const [itens, setItens] = useState<Item[]>([]);
  const [cadastrandoMaterial, setCadastrandoMaterial] = useState(false);
  const comFinanceiro = useFeature("financeiro");
  const podeFinanceiro = usePermissao("finance.manage");
  // A nota vai pra Notas fiscais, do financeiro; sem ele, fica só o número como texto.
  const comNota = comFinanceiro && podeFinanceiro;
  const [nota, setNota] = useState<NotaDoLancamentoValor | null>(null);
  const [valorDaNota, setValorDaNota] = useState<number | null>(null);
  const [pagamento, setPagamento] = useState<Pagamento>({ vencimento: "", pago: false, pagoEm: hojeEmTexto(), forma: "" });
  const fornecedores = trpc.partner.list.useQuery({ type: "SUPPLIER", take: 200, skip: 0 }, { enabled: aberto && comNota }).data?.partners ?? [];

  const materiaisQuery = trpc.prosthesis.catalog.myMaterialLines.useQuery(undefined, { enabled: aberto });
  const materiais = useMemo(
    () => (materiaisQuery.data ?? []).filter((m: any) => m.active !== false),
    [materiaisQuery.data],
  );
  const porId = useMemo(() => new Map(materiais.map((m: any) => [m.id, m])), [materiais]);
  // Com o catálogo carregado passa de cinquenta materiais; lista corrida obriga a ler tudo.
  const grupos = useMemo(() => agruparMateriais(materiais as any[]), [materiais]);

  /**
   * Semeia UMA vez por abertura.
   *
   * O efeito precisa de `materiais` (pra pegar o preço do material que veio da sugestão), mas a
   * lista muda quando alguém cadastra um material daqui de dentro — e sem esta trava o efeito
   * rodava de novo e apagava a nota que a pessoa estava montando.
   */
  const semeado = useRef(false);
  useEffect(() => {
    if (!aberto) {
      semeado.current = false;
      return;
    }
    if (semeado.current) return;
    // Com material vindo da sugestão, espera a lista chegar pra não semear sem o preço.
    if (materialInicial && materiais.length === 0) return;

    semeado.current = true;
    setFornecedor("");
    setDocumento("");
    setNota(null);
    setValorDaNota(null);
    setAtualizarPreco(true);
    setPagamento({ vencimento: "", pago: false, pagoEm: hojeEmTexto(), forma: "" });
    // Vindo da sugestão de compra, o material já entra com a quantidade sugerida — quem clicou ali
    // já decidiu o que comprar.
    if (materialInicial) {
      const m: any = porId.get(materialInicial.id);
      setItens([
        {
          materialLineId: materialInicial.id,
          nome: m?.name ?? "",
          quantidade: numeroParaTexto(Math.ceil((quantidadeInicial ?? materialInicial.sugerido) * 10000) / 10000),
          preco: m?.purchasePriceCents != null ? formatPriceCents(m.purchasePriceCents) : "",
        },
      ]);
      if (m?.supplier?.id) setFornecedor(m.supplier.id);
    } else {
      setItens([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, materialInicial?.id, materiais.length]);

  const registrar = trpc.estoque.registrarCompra.useMutation({
    onSuccess: (r) => {
      toast.success(`Entrada registrada com ${r.itens} ${r.itens === 1 ? "insumo" : "insumos"}.`);
      utils.estoque.invalidate();
      utils.prosthesis.catalog.myMaterialLines.invalidate();
      utils.financeiro.invalidate();
      onFechar();
    },
    onError: (e) => toast.error(e.message || "Não foi possível registrar a compra."),
  });

  const adicionar = (materialLineId: string) => {
    if (materialLineId === NOVO_MATERIAL) {
      setCadastrandoMaterial(true);
      return;
    }
    if (!materialLineId || itens.some((i) => i.materialLineId === materialLineId)) return;
    const m: any = porId.get(materialLineId);
    setItens((v) => [
      ...v,
      {
        materialLineId,
        nome: m?.name ?? "",
        quantidade: "1",
        preco: m?.purchasePriceCents != null ? formatPriceCents(m.purchasePriceCents) : "",
      },
    ]);
  };

  // XML da nota: o fornecedor pelo CNPJ e o valor, pra conferir com a soma dos itens.
  function aplicarDaNota(d: DadosDaNota) {
    const doc = soDigitos(d.emitente.documento);
    const achado = doc ? fornecedores.find((p: { document?: string | null }) => soDigitos(p.document) === doc) : undefined;
    if (achado) setFornecedor(achado.id);
    else if (d.emitente.nome) toast.info(`${d.emitente.nome} não está cadastrado como fornecedor. Escolha ou cadastre no campo Fornecedor.`);
    setValorDaNota(d.valorCents);
  }

  const valido =
    itens.length > 0 &&
    itens.every((i) => quantidadeParaNumero(i.quantidade) > 0 && i.preco.trim().length > 0) &&
    (!nota || nota.numero.trim().length > 0);

  const totalCents = itens.reduce(
    (s, i) => s + Math.round(quantidadeParaNumero(i.quantidade) * (parsePriceInput(i.preco) ?? 0)),
    0,
  );

  return (
    <>
      <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="flex max-h-[88dvh] max-w-2xl flex-col gap-3 overflow-y-auto dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_ESTOQUE.registrarEntrada}>Registrar entrada</DialogTitle>
          <DialogDescription className="dark:text-muted-foreground">
            O material recebido entra no estoque e, se você quiser, atualiza o preço de compra.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className={comNota ? "sm:col-span-2" : undefined}>
            <p className={labelCls}>Fornecedor</p>
            <FornecedorSelect valor={fornecedor} onChange={setFornecedor} />
          </div>
          {!comNota && (
            <div>
              <p className={labelCls}>Nota / documento</p>
              <input
                value={documento}
                onChange={(e) => setDocumento(e.target.value)}
                placeholder="Ex: NF 4412"
                className={inputCls}
              />
            </div>
          )}
        </div>
        {comNota && <NotaDoLancamento nota={nota} onChange={setNota} onLido={aplicarDaNota} />}

        <ItensDaCompra itens={itens} setItens={setItens} porId={porId} grupos={grupos} onAdicionar={adicionar} />

        <label className="flex items-start gap-2 rounded-md border border-zinc-200 p-2.5 dark:border-border">
          <input
            type="checkbox"
            checked={atualizarPreco}
            onChange={(e) => setAtualizarPreco(e.target.checked)}
            className="mt-0.5"
          />
          <span className="text-[12.5px] leading-snug text-zinc-700 dark:text-foreground/80">
            Atualizar o preço de compra destes materiais
            <span className="block text-[11px] text-zinc-500 dark:text-muted-foreground">
              Passa a valer para fichas e trabalhos novos. O custo de trabalho já concluído fica
              congelado, como está.
            </span>
          </span>
        </label>

        {comFinanceiro && <PagamentoDaCompra valor={pagamento} onChange={setPagamento} />}

        <div className="flex items-center justify-between gap-2 border-t border-zinc-100 pt-3 dark:border-border">
          <span className="text-xs text-zinc-600 dark:text-muted-foreground">
            Total <strong className="ml-1 tabular-nums text-zinc-900 dark:text-foreground">{formatPriceCents(totalCents)}</strong>
            {nota && valorDaNota != null && valorDaNota !== totalCents && (
              <span className="ml-2 text-amber-700 dark:text-amber-400">Nota: {formatPriceCents(valorDaNota)}</span>
            )}
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              onClick={onFechar}
              className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-sm font-medium text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={!valido || registrar.isPending}
              onClick={() =>
                registrar.mutate({
                  supplierPartnerId: fornecedor || null,
                  documento: comNota ? (nota ? `NF ${nota.numero.trim()}` : null) : documento.trim() || null,
                  atualizarPreco,
                  ...(comFinanceiro && {
                    vencimento: pagamento.vencimento || null,
                    pagoEm: pagamento.pago ? pagamento.pagoEm : null,
                    formaDePagamento: pagamento.pago && pagamento.forma ? pagamento.forma : null,
                  }),
                  ...(comNota &&
                    nota && {
                      nota: {
                        numero: nota.numero.trim(),
                        serie: nota.serie,
                        chaveDeAcesso: nota.chaveDeAcesso,
                        emitidaEm: nota.emitidaEm,
                        fileIds: nota.arquivos.map((a) => a.fileId),
                      },
                    }),
                  itens: itens.map((i) => ({
                    materialLineId: i.materialLineId,
                    quantity: quantidadeParaNumero(i.quantidade),
                    unitCostCents: parsePriceInput(i.preco) ?? 0,
                  })),
                })
              }
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
            >
              {registrar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Registrar entrada
            </button>
          </span>
        </div>
      </DialogContent>
      </Dialog>

      {/* Reusa o cadastro de material inteiro em vez de um mini-formulário: material exige
          subcategoria, fabricante e nome, e uma segunda validação viveria desatualizada. */}
      <MaterialLineDialog
        open={cadastrandoMaterial}
        onClose={() => setCadastrandoMaterial(false)}
        onCriado={(m) => {
          // Já entra na nota: quem acabou de cadastrar não deveria procurar na lista o que criou.
          setItens((v) =>
            v.some((i) => i.materialLineId === m.id)
              ? v
              : [
                  ...v,
                  {
                    materialLineId: m.id,
                    nome: m.name,
                    quantidade: "1",
                    // O preço acabou de ser digitado no cadastro; pedir de novo seria trabalho à toa.
                    preco: m.purchasePriceCents != null ? formatPriceCents(m.purchasePriceCents) : "",
                  },
                ],
          );
        }}
      />
    </>
  );
}
